import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFacebookF, 
  faInstagram, 
  faTwitter, 
  faSpotify, 
  faYoutube 
} from '@fortawesome/free-brands-svg-icons';
import { ReactComponent as LogoPoney } from '../assets/images/poneyfooter.svg';
import { faEnvelope, faPhone, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { fetchOptions, fetchFood } from '../api/api';
import styles from '../styles/components/_Footer.module.scss';
import { useCustomNavigation } from './NavigationContext';
import NewsletterModal from './NewsletterModal';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const { customNavigate } = useCustomNavigation();
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);

  // Récupération des données de l'API
  const { data: options } = useQuery({
    queryKey: ['options'],
    queryFn: fetchOptions,
  });

  const { data: foods = [] } = useQuery({
    queryKey: ['foods'],
    queryFn: fetchFood,
    staleTime: 5 * 60 * 1000,
  });

  const socialLinks = options?.a_propos?.reseaux_sociaux || [];

  const getSocialIcon = (name) => {
    switch (name.toLowerCase()) {
      case 'facebook':
        return faFacebookF;
      case 'instagram':
        return faInstagram;
      case 'twitter':
        return faTwitter;
      case 'spotify':
        return faSpotify;
      case 'youtube':
        return faYoutube;
      default:
        return null;
    }
  };

  const handleMenuClick = (path) => {
    customNavigate(path);
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.footerContent}>
        <div className={styles.footerContainer}>
          {/* Colonne Logo et Description */}
          <div className={styles.footerColumn}>
            <a href="/" className={styles.footerLogo} onClick={(e) => {
              e.preventDefault();
              handleMenuClick('/');
            }}>
              <LogoPoney className={styles.logoSvg} />
            </a>
            <p className={styles.festivalDesc}>
              {options?.seo?.description || "Le Poney Club Festival, c'est l'événement musical incontournable qui mélange musique électronique et ambiance festive unique."}
            </p>
          </div>

          {/* Colonne Contact */}
          <div className={styles.footerColumn}>
            <h3 className={styles.columnTitle}>Contact</h3>
            <ul className={styles.contactList}>
              <li>
                <FontAwesomeIcon icon={faEnvelope} />
                <a href="mailto:contact@poneyclubtoulouse.fr">contact@poneyclubtoulouse.fr</a>
              </li>
              <li>
                <FontAwesomeIcon icon={faPhone} />
                <a href="tel:+33677903227">06 77 90 32 27</a>
              </li>
              <li>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span>Parking Aéroport Res@P3, 31700 Blagnac</span>
              </li>
            </ul>
          </div>

          {/* Colonne Navigation */}
          <div className={styles.footerColumn}>
            <h3 className={styles.columnTitle}>Navigation</h3>
            <div className={styles.linksGrid}>
              <Link
                to="/programmation"
                className={location.pathname === '/programmation' ? styles.active : ''}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick('/programmation');
                }}
              >
                programmation
              </Link>
              {foods.length > 0 && (
                <a 
                  href="/food" 
                  className={location.pathname === '/food' ? styles.active : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick('/food');
                  }}
                >
                  Food
                </a>
              )}
              <a 
                href="/billetterie" 
                className={location.pathname === '/billetterie' ? styles.active : ''}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick('/billetterie');
                }}
              >
                Billetterie
              </a>
              <a 
                href="/contact" 
                className={location.pathname === '/contact' ? styles.active : ''}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick('/contact');
                }}
              >
                Contact
              </a>
            </div>
          </div>

          {/* Colonne Newsletter */}
          <div className={styles.footerColumn}>
            <h3 className={styles.columnTitle}>Newsletter</h3>
            <div className={styles.newsletterSection}>
              <p>Restez informé de nos actualités</p>
              <button 
                className={`custom-button custom-button--light ${styles.newsletterButton}`}
                onClick={() => setIsNewsletterModalOpen(true)}
              >
                S'inscrire à la newsletter
              </button>
            </div>
            <div className={styles.socialIcons}>
              {socialLinks.map((link) => {
                const icon = getSocialIcon(link.nom);
                if (!icon) return null;
                
                return (
                  <a 
                    key={link.nom}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.nom}
                  >
                    <FontAwesomeIcon icon={icon} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

      
      </div>

      <NewsletterModal 
        isOpen={isNewsletterModalOpen}
        onClose={() => setIsNewsletterModalOpen(false)}
      />
    </footer>
  );
};

export default Footer;
